import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { MOBILE_WIDTH } from 'const';
import {
  FEBottomSheet,
  FEButton,
  FENotificationCard,
  FERadioButton,
  FESegmentedControl,
  FESpinner,
} from 'components';
import { useMediaQuery } from 'ui-kit/hooks';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { OrderType } from 'models';
import { IMG_IC_24_DELETE } from 'images';
import {
  $savedAddresses,
  deleteSavedAddressFx,
  getSavedAddressesFx,
  SavedAddress,
} from 'domains/savedAddresses';
import {
  $address,
  $allCitiesList,
  $citiesList,
  $city,
  $countriesList,
  $country,
  $userGeolocation,
  changeCity,
  changeCountry,
  submitAddress,
} from 'domains/cartography';
import { closeStartPage } from 'domains/application';
import { RestaurantLayout } from '..';
import {
  formatAddressDetails,
  formatAddressPrimary,
  isSameDeliveryAddress,
} from 'pages/SavedAddresses/lib/formatAddress';
import style from './style.module.css';

type OrderTab = Exclude<OrderType, 'IN_PLACE'>;

type Props = {
  open: boolean;
  onClose: () => void;
  onAddAddress: () => void;
  onEmpty: () => void;
};

export const SavedAddressesPicker = ({
  open,
  onClose,
  onAddAddress,
  onEmpty,
}: Props) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(`(min-width: ${MOBILE_WIDTH}px)`);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pendingDeleteRef = useRef<SavedAddress | null>(null);
  const ignoreCloseRef = useRef(false);
  const hadAddressesRef = useRef(false);
  const [tab, setTab] = useState<OrderTab>('COURIER');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thumb, setThumb] = useState({ height: 0, top: 0, visible: false });
  const [pendingDelete, setPendingDelete] = useState<SavedAddress | null>(
    null,
  );
  pendingDeleteRef.current = pendingDelete;

  const [
    addresses,
    isPending,
    currentAddress,
    city,
    country,
    citiesList,
    allCitiesList,
    countriesList,
    userGeolocation,
    onChangeCity,
    onChangeCountry,
    onSubmitAddress,
    onCloseStartPage,
    deleteAddress,
  ] = useUnit([
    $savedAddresses,
    getSavedAddressesFx.pending,
    $address,
    $city,
    $country,
    $citiesList,
    $allCitiesList,
    $countriesList,
    $userGeolocation,
    changeCity,
    changeCountry,
    submitAddress,
    closeStartPage,
    deleteSavedAddressFx,
  ]);

  useEffect(() => {
    if (!open) {
      hadAddressesRef.current = false;
      setPendingDelete(null);
      return;
    }
    if (addresses.length > 0) hadAddressesRef.current = true;
  }, [open, addresses.length]);

  useEffect(() => {
    if (!open || isPending || addresses.length > 0) return;
    if (hadAddressesRef.current) return;
    onEmpty();
  }, [open, isPending, addresses.length, onEmpty]);

  const requestClose = () => {
    if (pendingDeleteRef.current || ignoreCloseRef.current) return;
    onClose();
  };

  const dismissDelete = () => {
    ignoreCloseRef.current = true;
    pendingDeleteRef.current = null;
    setPendingDelete(null);
    window.setTimeout(() => {
      ignoreCloseRef.current = false;
    }, 350);
  };

  useEffect(() => {
    if (!open || !isDesktop) return;
    const onPointerDown = (event: PointerEvent) => {
      if (pendingDeleteRef.current || ignoreCloseRef.current) return;
      const anchor = anchorRef.current;
      if (!anchor) return;
      const target = event.target as Node;
      if (anchor.contains(target)) return;
      if (anchor.parentElement?.contains(target)) return;
      if (target instanceof Element && target.closest('[role="dialog"]')) {
        return;
      }
      onClose();
    };
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, isDesktop, onClose]);

  const updateThumb = () => {
    const list = listRef.current;
    if (!list) return;
    const { scrollHeight, clientHeight, scrollTop } = list;
    if (scrollHeight <= clientHeight + 1) {
      setThumb({ height: 0, top: 0, visible: false });
      return;
    }
    const height = Math.max((clientHeight / scrollHeight) * clientHeight, 24);
    const maxTop = clientHeight - height;
    const top =
      ((scrollTop / (scrollHeight - clientHeight)) * maxTop) || 0;
    setThumb({ height, top, visible: true });
  };

  useEffect(() => {
    if (!open) return;
    getSavedAddressesFx();
  }, [open]);

  useEffect(() => {
    updateThumb();
  }, [open, addresses, tab]);

  useEffect(() => {
    if (!open || !addresses.length) {
      if (!addresses.length) setSelectedId(null);
      return;
    }
    const current = addresses.find((item) =>
      isSameDeliveryAddress(item, currentAddress),
    );
    setSelectedId(current?.id ?? addresses[0].id);
  }, [open, addresses, currentAddress]);

  const detailLabels = {
    entrance: t('profile.addressEntrance'),
    floor: t('profile.addressFloor'),
    apartment: t('profile.addressApartment'),
    housing: t('profile.addressHousing'),
  };

  const applySelected = () => {
    const item = addresses.find((address) => address.id === selectedId);
    if (!item) return;

    if (city?.id !== item.cityId) {
      const nextCity =
        allCitiesList.find((c) => c.id === item.cityId) ||
        citiesList.find((c) => c.id === item.cityId);

      if (nextCity) {
        if (country?.id !== nextCity.countryId) {
          const nextCountry = countriesList.find(
            (c) => c.id === nextCity.countryId,
          );
          if (nextCountry) onChangeCountry(nextCountry);
        }
        onChangeCity(nextCity);
      }
    }

    onSubmitAddress({
      currentAddress: {
        address: item.address,
        street: item.street,
        house: item.house,
        housing: item.housing,
        cityId: item.cityId,
        location: item.location,
        details: item.details,
        entrance: item.entrance,
        apartment: item.apartment,
        floor: item.floor,
      },
    });
    onCloseStartPage();
    onClose();
  };

  const deleteDescription = pendingDelete
    ? [
        formatAddressPrimary(pendingDelete),
        formatAddressDetails(pendingDelete, detailLabels),
      ]
        .filter(Boolean)
        .join('. ')
    : '';

  const content = (
    <div
      ref={anchorRef}
      className={clsx(
        isDesktop ? style.anchor : style.sheet,
        tab === 'GO_TO_PLACE' && style.restaurant,
      )}
    >
      <div className={style.segmentedControl}>
        <FESegmentedControl<OrderTab>
          items={{
            COURIER: t('cartography.segmentedControl.delivery'),
            GO_TO_PLACE: t('cartography.segmentedControl.pickup'),
          }}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'COURIER' ? (
        <>
          {isPending && !addresses.length ? (
            <div className={style.loader}>
              <FESpinner />
            </div>
          ) : (
            <div className={style.listWrap}>
              <div
                ref={listRef}
                className={style.list}
                onScroll={updateThumb}
              >
              {addresses.map((item) => {
                const details = formatAddressDetails(item, detailLabels);
                return (
                  <div
                    key={item.id}
                    className={style.row}
                    onClick={() => setSelectedId(item.id)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedId(item.id);
                      }
                    }}
                  >
                    <div className={style.radio}>
                      <FERadioButton active={selectedId === item.id} />
                    </div>
                    <div className={style.text}>
                      <p className={clsx('text-1', style.primary)}>
                        {formatAddressPrimary(item)}
                      </p>
                      {!!details && (
                        <p className={clsx('caption-1', style.secondary)}>
                          {details}
                        </p>
                      )}
                    </div>
                    <button
                      type='button'
                      className={style.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(item);
                      }}
                      aria-label={t('cartography.addressPicker.delete')}
                    >
                      <img
                        className={style.deleteIcon}
                        src={IMG_IC_24_DELETE}
                        alt=''
                      />
                    </button>
                  </div>
                );
                })}
              </div>
              {thumb.visible && (
                <div
                  className={style.thumb}
                  style={{
                    height: thumb.height,
                    transform: `translateY(${thumb.top}px)`,
                  }}
                />
              )}
            </div>
          )}
          <div className={style.actions}>
            <FEButton disabled={!selectedId} onClick={applySelected} className={style.confirmButton}>
              {t('cartography.addressPicker.confirm')}
            </FEButton>
            <FEButton type='secondary' onClick={onAddAddress}>
              {t('profile.addressesAdd')}
            </FEButton>
          </div>
        </>
      ) : (
        <RestaurantLayout
          fromCart={false}
          selectOnRowClick
          currentCity={city}
          userGeolocation={userGeolocation}
          changeType={setTab}
          onClose={onClose}
          onCloseModal={onClose}
        />
      )}

      <FENotificationCard
        open={!!pendingDelete}
        icon='error'
        title={t('cartography.addressPicker.deleteTitle')}
        description={deleteDescription}
        onClose={dismissDelete}
        primaryButtonProps={{
          text: t('cartography.addressPicker.delete'),
          closeOnClick: true,
          onClick: () => {
            if (pendingDelete) deleteAddress(pendingDelete.id);
          },
        }}
        secondaryButtonProps={{
          text: t('cartography.addressPicker.cancel'),
          closeOnClick: true,
          onClick: dismissDelete,
        }}
      />
    </div>
  );

  if (!isDesktop) {
    return (
      <FEBottomSheet
        open={open}
        onClose={requestClose}
        closeOnOverlayClick
        render={() => content}
      />
    );
  }

  if (!open) return null;

  return content;
};
